import { useEffect, useState, useSyncExternalStore } from "react";

const KEY = "liftquest.debugViewport";
const listeners = new Set<() => void>();

function read() {
  try {
    return localStorage.getItem(KEY) === "1";
  } catch {
    return false;
  }
}

export function toggleViewportDebug() {
  try {
    localStorage.setItem(KEY, read() ? "0" : "1");
  } catch {
    // ignore
  }
  listeners.forEach((l) => l());
}

function useEnabled() {
  return useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    read,
    () => false,
  );
}

function insets() {
  const probe = document.createElement("div");
  probe.style.cssText =
    "position:fixed;visibility:hidden;padding:env(safe-area-inset-top) env(safe-area-inset-right) env(safe-area-inset-bottom) env(safe-area-inset-left)";
  document.body.appendChild(probe);
  const cs = getComputedStyle(probe);
  const v = [cs.paddingTop, cs.paddingRight, cs.paddingBottom, cs.paddingLeft].map((x) => Math.round(parseFloat(x)));
  probe.remove();
  return v;
}

function measure() {
  const rect = (sel: string) => {
    const r = document.querySelector(sel)?.getBoundingClientRect();
    return r ? `${Math.round(r.top)}–${Math.round(r.bottom)}` : "–";
  };
  const vv = window.visualViewport;
  return {
    standalone: document.documentElement.classList.contains("ios-standalone") ? "ios" : String(matchMedia("(display-mode: standalone)").matches),
    screen: `${screen.width}×${screen.height}`,
    inner: `${innerWidth}×${innerHeight}`,
    client: `${document.documentElement.clientWidth}×${document.documentElement.clientHeight}`,
    visual: vv ? `${Math.round(vv.width)}×${Math.round(vv.height)} @${Math.round(vv.offsetTop)}` : "–",
    "safe t/r/b/l": insets().join("/"),
    gap: getComputedStyle(document.documentElement).getPropertyValue("--app-gap") || "–",
    navPadB: (() => {
      const n = document.querySelector(".app-shell > nav");
      return n ? getComputedStyle(n).paddingBottom : "–";
    })(),
    shell: rect(".app-shell"),
    nav: rect(".app-shell > nav"),
    dpr: String(devicePixelRatio),
  };
}

/** Hidden overlay with viewport numbers, toggled by tapping the version 5× on the profile page. */
export function ViewportDebug() {
  const enabled = useEnabled();
  const [values, setValues] = useState<Record<string, string> | null>(null);
  useEffect(() => {
    if (!enabled) return;
    const update = () => setValues(measure());
    update();
    const id = setInterval(update, 500);
    return () => clearInterval(id);
  }, [enabled]);
  if (!enabled || !values) return null;
  return (
    <div className="pointer-events-auto fixed top-1/3 right-2 z-[100] rounded-xl bg-black/85 p-2.5 font-mono text-[11px] leading-snug text-white shadow-lg">
      {Object.entries(values).map(([k, v]) => (
        <div key={k}>
          <span className="text-white/60">{k}:</span> {v}
        </div>
      ))}
      <button type="button" className="mt-1.5 w-full rounded bg-white/15 py-1" onClick={toggleViewportDebug}>
        close
      </button>
    </div>
  );
}
